import { fromZonedTime, formatInTimeZone } from 'date-fns-tz';

/**
 * Date / time helpers for `<input type="datetime-local">` round-trips
 * and 12-hour `<TimeInput>` cells.
 *
 * The native `datetime-local` input is timezone-naive — it produces
 * and consumes strings shaped like `YYYY-MM-DDTHH:mm`, interpreted in
 * the user's local timezone. App backends typically store instants
 * as ISO 8601 with offset (UTC). Naively bridging the two with
 * `.toISOString()` silently shifts every value by the user's offset
 * on each round trip.
 *
 * These helpers do the conversion explicitly **in a caller-provided
 * timezone** (e.g. an event's IANA zone), so a 2pm America/New_York
 * value stays at 2pm America/New_York whether the user is in EDT,
 * PST, or UTC.
 */
/**
 * Get the IANA timezone the user's browser is currently in.
 * Used as a sane default when a record hasn't picked one yet.
 */
function detectBrowserTimezone() {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}
/**
 * Convert a UTC ISO 8601 instant to the value an
 * `<input type="datetime-local">` expects, *in the given timezone*.
 *
 *   toDatetimeLocalValue('2026-06-01T18:00:00Z', 'America/New_York')
 *     → '2026-06-01T14:00'   // 2pm EDT
 *
 * Returns an empty string for null / undefined / invalid input so the
 * input renders empty.
 */
function toDatetimeLocalValue(iso, timezone) {
    if (!iso)
        return '';
    try {
        return formatInTimeZone(iso, timezone, "yyyy-MM-dd'T'HH:mm");
    }
    catch {
        return '';
    }
}
/**
 * Inverse of {@link toDatetimeLocalValue} — convert the value a user
 * typed into a `datetime-local` input back to a UTC ISO 8601 instant,
 * interpreting the input in the given timezone.
 *
 *   fromDatetimeLocalValue('2026-06-01T14:00', 'America/New_York')
 *     → '2026-06-01T18:00:00.000Z'
 *
 * Returns null for empty / invalid input.
 */
function fromDatetimeLocalValue(value, timezone) {
    if (!value)
        return null;
    try {
        const date = fromZonedTime(value, timezone);
        if (Number.isNaN(date.getTime()))
            return null;
        return date.toISOString();
    }
    catch {
        return null;
    }
}
/**
 * Split a `<input type="datetime-local">`-shaped value into its
 * date portion (a Date object at local midnight, suitable for the
 * shared `<DatePicker useUtc={false}>`) and its time portion
 * (`"HH:mm"`, suitable for `<TimeInput>`).
 *
 *   splitDatetimeLocalValue('2026-06-01T14:00')
 *     → { date: <Date 2026-06-01 00:00 local>, time: '14:00' }
 *
 * Returns `{ date: undefined, time: '' }` for empty / malformed input.
 */
function splitDatetimeLocalValue(value) {
    if (!value)
        return { date: undefined, time: '' };
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
    if (!match)
        return { date: undefined, time: '' };
    const [, y, mo, d, hh, mm] = match;
    // Construct a Date at local midnight on (y, mo, d). The time portion
    // of this Date is never read — it's purely a calendar marker for the
    // date picker.
    const date = new Date(Number(y), Number(mo) - 1, Number(d));
    return { date, time: `${hh}:${mm}` };
}
/**
 * Inverse of {@link splitDatetimeLocalValue}.
 *
 *   joinDatetimeLocalValue(<Date 2026-06-01>, '14:00')
 *     → '2026-06-01T14:00'
 *
 * Reads the date's *local* y/mo/d (not UTC) so that the round-trip
 * with split() preserves the calendar day the user picked. Returns
 * an empty string if either piece is missing.
 */
function joinDatetimeLocalValue(date, time) {
    if (!date || !time)
        return '';
    const y = date.getFullYear();
    const mo = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    // `time` already comes in HH:mm or HH:mm:ss — keep just the HH:mm part.
    const hhmm = time.slice(0, 5);
    return `${y}-${mo}-${d}T${hhmm}`;
}
/**
 * Parse a 24-hour `"HH:mm"` string into 12-hour pieces, suitable for
 * the segmented `<TimeInput>` (hour cell + minute cell + AM/PM toggle).
 *
 *   parse12HourTime('14:30') → { hour12: 2, minute: 30, period: 'PM' }
 *   parse12HourTime('00:00') → { hour12: 12, minute: 0, period: 'AM' }
 *
 * Returns `{ hour12: null, minute: null, period: 'AM' }` for empty
 * or unparseable input.
 */
function parse12HourTime(value) {
    if (!value)
        return { hour12: null, minute: null, period: 'AM' };
    const match = value.match(/^(\d{1,2}):(\d{2})/);
    if (!match)
        return { hour12: null, minute: null, period: 'AM' };
    const h = Number(match[1]);
    const m = Number(match[2]);
    if (Number.isNaN(h) || Number.isNaN(m) || h < 0 || h > 23 || m < 0 || m > 59) {
        return { hour12: null, minute: null, period: 'AM' };
    }
    const period = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 === 0 ? 12 : h % 12;
    return { hour12, minute: m, period };
}
/**
 * Inverse of {@link parse12HourTime}: format 12-hour pieces back to
 * the canonical 24-hour `"HH:mm"`. Returns `''` if either piece is
 * missing — callers usually treat empty as "no time selected".
 */
function format12HourTime(hour12, minute, period) {
    if (hour12 === null || minute === null)
        return '';
    let h24 = hour12 % 12;
    if (period === 'PM')
        h24 += 12;
    return `${String(h24).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}
/**
 * Build a human-readable label for an IANA timezone, including the
 * current UTC offset:
 *
 *   formatTimezoneLabel('America/New_York') // -> "America/New_York (UTC−04:00)"
 *   formatTimezoneLabel('UTC')              // -> "UTC (UTC+00:00)"
 *
 * The offset reflects the *current* moment, so during DST changes
 * the same zone may format differently at different times.
 */
function formatTimezoneLabel(timezone) {
    let offset = '';
    try {
        const parts = new Intl.DateTimeFormat('en-US', {
            timeZone: timezone,
            timeZoneName: 'longOffset',
        }).formatToParts(new Date());
        const tzPart = parts.find((p) => p.type === 'timeZoneName')?.value;
        if (tzPart) {
            // `longOffset` gives "GMT-04:00" — switch to "UTC±HH:MM" and use
            // the typographic minus so it reads cleanly in a select.
            offset = tzPart.replace('GMT', 'UTC').replace('-', '−');
            // Normalize "UTC" (no offset) to "UTC+00:00".
            if (offset === 'UTC')
                offset = 'UTC+00:00';
        }
    }
    catch {
        // fall through — return the bare zone
    }
    return offset ? `${timezone} (${offset})` : timezone;
}

export { detectBrowserTimezone, format12HourTime, formatTimezoneLabel, fromDatetimeLocalValue, joinDatetimeLocalValue, parse12HourTime, splitDatetimeLocalValue, toDatetimeLocalValue };
//# sourceMappingURL=datetime.js.map
